/**
 * ConnectionBar - Persistent connection status bar at top of window
 * Always visible regardless of which tab is active
 */

import { useState, useEffect, useRef } from 'react';
import {
  type ConnectionState,
  type SerialConnection,
  getSerialConnection,
} from '@/lib/serialConnection';

export function ConnectionBar() {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [deviceName, setDeviceName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const connectionRef = useRef<SerialConnection | null>(null);

  // Setup connection callbacks on mount
  useEffect(() => {
    const conn = getSerialConnection();
    connectionRef.current = conn;

    // Subscribe to state changes
    const unsubscribe = conn.onStateChange((state) => {
      setConnectionState(state);
      if (state === 'disconnected') {
        setDeviceName('');
      }
      if (state === 'error') {
        setError('Connection lost');
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleConnect = async () => {
    const conn = connectionRef.current;
    if (!conn) return;

    setError(null);
    try {
      const connected = await conn.connect();
      if (connected) {
        // Fetch device name
        const response = await conn.sendCommand('version');
        const match = response.raw.match(/Firmware:\s*(.+)/);
        if (match?.[1]) {
          setDeviceName(match[1].trim());
        } else {
          setDeviceName('STAK MIDI');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
    }
  };

  const handleDisconnect = async () => {
    const conn = connectionRef.current;
    if (!conn) return;

    try {
      await conn.disconnect();
    } catch (err) {
      console.error('Disconnect failed:', err);
    }
    setDeviceName('');
  };

  const isConnected = connectionState === 'connected';
  const isConnecting = connectionState === 'connecting';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '6px 16px',
        backgroundColor: isConnected ? '#d4edda' : '#f8f9fa',
        borderBottom: `1px solid ${isConnected ? '#c3e6cb' : '#dee2e6'}`,
        fontSize: '12px',
        transition: 'background-color 0.2s',
      }}
    >
      {/* Status indicator */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: isConnected ? '#28a745' : isConnecting ? '#ffc107' : '#dc3545',
            boxShadow: isConnected ? '0 0 4px #28a745' : 'none',
          }}
        />
        <span style={{ fontWeight: 500, color: isConnected ? '#155724' : '#495057' }}>
          {isConnected ? 'Connected' : isConnecting ? 'Connecting...' : 'Disconnected'}
        </span>
      </div>

      {/* Device name when connected */}
      {isConnected && deviceName && (
        <span style={{ color: '#155724' }}>
          — {deviceName}
        </span>
      )}

      {/* Error message */}
      {error && (
        <span style={{ color: '#dc3545', marginLeft: 'auto' }}>
          {error}
        </span>
      )}

      {/* Connect/Disconnect button */}
      <div style={{ marginLeft: 'auto' }}>
        {!isConnected ? (
          <button
            onClick={handleConnect}
            disabled={isConnecting}
            style={{
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: 500,
              color: '#fff',
              backgroundColor: '#007bff',
              border: 'none',
              borderRadius: '4px',
              cursor: isConnecting ? 'wait' : 'pointer',
              opacity: isConnecting ? 0.7 : 1,
            }}
          >
            {isConnecting ? 'Connecting...' : 'Connect Device'}
          </button>
        ) : (
          <button
            onClick={handleDisconnect}
            style={{
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: 500,
              color: '#fff',
              backgroundColor: '#6c757d',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Disconnect
          </button>
        )}
      </div>
    </div>
  );
}
